import * as React from 'react'
import NumberFieldInnerTable from '../../inputs/NumberFieldInnerTable'
import {
  ContractDetailsModel,
  CustomAddition,
  CustomAdditionType,
} from '@/modules/drivers/types/contracts'
import RateReduceCustom from '../shared/RateReduceCustom'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

interface CustomAdditionsProps {
  data: ContractDetailsModel
  setData: (data: ContractDetailsModel) => void
}

const CustomAdditions: React.FC<CustomAdditionsProps> = ({ data, setData }) => {
  const { t } = useLocalize()
  const customAdditions = data?.customAdditions || []

  const addNewAddition = () => {
    const newAddition: CustomAddition = {
      id: Date.now().toString(),
      name: '',
      value: 0,
      additionType: CustomAdditionType.Month,
    }
    setData({
      ...data,
      customAdditions: [...customAdditions, newAddition],
    })
  }

  const removeAddition = (id: string) => {
    setData({
      ...data,
      customAdditions: customAdditions.filter((item) => item.id !== id),
    })
  }

  const updateAddition = (
    id: string,
    field: keyof CustomAddition,
    value: any,
  ) => {
    setData({
      ...data,
      customAdditions: customAdditions.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    })
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-px-5 tw-pb-3 tw-pt-3">
      {customAdditions.length === 0 && (
        <div className="tw-text-content-subtle tw-text-xs tw-italic">
          {t('contract.no-additions')}
        </div>
      )}

      {customAdditions.map((addition, index) => (
        <div
          key={addition.id}
          className="tw-border-border-default tw-bg-background-subtle tw-flex tw-flex-col tw-gap-2 tw-rounded tw-border tw-p-3"
        >
          <div className="tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-content-default tw-text-xs tw-font-medium">
              {t('contract.component')} #{index + 1}
            </span>
            <button
              type="button"
              onClick={() => removeAddition(addition.id!)}
              className="tw-text-xs tw-text-red-600 tw-underline hover:tw-text-red-700"
            >
              {t('common.delete')}
            </button>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <label className="tw-text-content-subtle tw-text-xs">
              {t('contract.component-name')}
            </label>
            <input
              type="text"
              className="tw-border-border-default tw-bg-background-default tw-w-full tw-rounded tw-border tw-px-3 tw-py-2 tw-text-sm"
              placeholder={t('contract.component-name-placeholder')}
              value={addition.name}
              onChange={(e) =>
                updateAddition(addition.id!, 'name', e.target.value)
              }
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <label className="tw-text-content-subtle tw-text-xs">{t('contract.value')}</label>
            <NumberFieldInnerTable
              innerLabel="PLN"
              placeholder="0,00"
              value={addition.value?.toString() || ''}
              onChange={(e) =>
                updateAddition(addition.id!, 'value', Number(e.target.value))
              }
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <label className="tw-text-content-subtle tw-text-xs">
              {t('contract.settlement-type')}
            </label>
            <select
              className="tw-border-border-default tw-bg-background-default tw-w-full tw-rounded tw-border tw-px-3 tw-py-2 tw-text-sm"
              value={addition.additionType ?? CustomAdditionType.Month}
              onChange={(e) =>
                updateAddition(
                  addition.id!,
                  'additionType',
                  Number(e.target.value),
                )
              }
            >
              <option value={CustomAdditionType.Month}>{t('contract.monthly')}</option>
              <option value={CustomAdditionType.Day}>{t('contract.daily')}</option>
            </select>
          </div>

          {(addition.additionType === CustomAdditionType.Month ||
            addition.additionType === undefined) && (
            <RateReduceCustom
              background
              data={
                {
                  ...data,
                  reduceCustomAdditionDev: addition.reduceCustomAddition,
                  reduceCustomAdditionForSickLeave:
                    addition.reduceCustomAdditionForSickLeave,
                  reduceCustomAdditionForLeave:
                    addition.reduceCustomAdditionForLeave,
                  reduceCustomAdditionForNotPaidLeave:
                    addition.reduceCustomAdditionForNotPaidLeave,
                } as any
              }
              setData={(newData: any) => {
                const updatedAdditions = customAdditions.map((item) =>
                  item.id === addition.id
                    ? {
                        ...item,
                        reduceCustomAddition: newData.reduceCustomAdditionDev,
                        reduceCustomAdditionForSickLeave:
                          newData.reduceCustomAdditionForSickLeave,
                        reduceCustomAdditionForLeave:
                          newData.reduceCustomAdditionForLeave,
                        reduceCustomAdditionForNotPaidLeave:
                          newData.reduceCustomAdditionForNotPaidLeave,
                      }
                    : item,
                )
                setData({
                  ...data,
                  customAdditions: updatedAdditions,
                })
              }}
              reduceType="additional"
              mainCheckboxProperty="reduceCustomAddition"
              sickLeaveProperty="reduceCustomAdditionForSickLeave"
              paidLeaveProperty="reduceCustomAdditionForLeave"
              unpaidLeaveProperty="reduceCustomAdditionForNotPaidLeave"
            />
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={addNewAddition}
        className="tw-w-full tw-rounded tw-bg-blue-600 tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-transition-colors hover:tw-bg-blue-700"
      >
        + {t('contract.add-component')}
      </button>
    </div>
  )
}

export default CustomAdditions
